import React, { useState } from "react";
import axios from "../utils/axios";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";

const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required("Email is Required")
    .email("Email should be valid"),
});

const otpSchema = yup.object({
  otp: yup
    .string()
    .required("OTP is required")
    .matches(/^\d{6}$/, "OTP should be a 6-digit number"),
});

const passwordSchema = yup.object({
  newPassword: yup
    .string()
    .required("New password is required")
    .min(6, "Password should be at least 6 characters long"),
});

const ForgotPassword = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const navigate = useNavigate();

  // Email Formik
  const emailFormik = useFormik({
    initialValues: { email: "" },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      try {
        const response = await axios.post("user/forgot-password", {
          email: values.email,
        });
        toast.success(response.data.message);
        setEmail(values.email);
        setOtpMode(true);
      } catch (error) {
        toast.error(
          "Error: " + (error.response?.data?.message || "Something went wrong"),
        );
      }
    },
  });

  // OTP Formik
  const otpFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      try {
        const response = await axios.post("user/verify-otp", {
          email,
          otp: values.otp,
        });
        toast.success(
          "OTP verified successfully! Please enter a new password.",
        );
        setOtpVerified(true);
      } catch (error) {
        toast.error(
          "Error: " + (error.response?.data?.message || "Invalid OTP"),
        );
      }
    },
  });

  // New Password Formik
  const passwordFormik = useFormik({
    initialValues: { newPassword: "" },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      try {
        const response = await axios.post("user/reset-password", {
          email,
          newPassword: values.newPassword,
        });
        toast.success("Password reset successfully! Redirecting to login...");
        navigate("/login");
      } catch (error) {
        toast.error(
          "Error: " + (error.response?.data?.message || "Something went wrong"),
        );
      }
    },
  });

  return (
    <div>
      <main
        className="main__content_wrapper min-h-screen"
        style={{
          background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
        }}
      >
        {/* Start forgot password section */}
        <div className="w-full flex items-center justify-center py-20 px-4">
          {/* Centered Form Card */}
          <div className="w-full max-w-lg mx-auto bg-white/95 backdrop-blur-md shadow-2xl rounded-3xl p-8 sm:p-10 border border-white/10">
            {/* Form Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-[#0d9488] mb-2 uppercase tracking-wide">
                {otpVerified
                  ? "Secure Account"
                  : otpMode
                    ? "Verification"
                    : "Recover Access"}
              </h2>
              <p className="text-gray-500 font-medium tracking-tight">
                {otpVerified
                  ? "Please set a secure new password for your Pulse Addis account."
                  : otpMode
                    ? "We have sent a secure code to your email. Please enter it below."
                    : "Enter your registered email to begin the recovery process or "}
                {!otpVerified && !otpMode && (
                  <Link
                    to="/"
                    className="text-teal-600 font-bold hover:underline"
                  >
                    return to site
                  </Link>
                )}
              </p>
            </div>

            {/* Form Inner */}
            <div className="account__login--inner">
              {!otpMode ? (
                <form onSubmit={emailFormik.handleSubmit}>
                  <div className="mb-6">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="h-5 w-5 text-teal-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <input
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                        placeholder="Email Address"
                        type="email"
                        name="email"
                        value={emailFormik.values.email}
                        onChange={emailFormik.handleChange}
                        onBlur={emailFormik.handleBlur}
                      />
                    </div>
                    {emailFormik.touched.email && emailFormik.errors.email && (
                      <div className="error text-red-500 text-sm mt-2 font-medium">
                        {emailFormik.errors.email}
                      </div>
                    )}
                  </div>

                  <button
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-white font-bold text-lg shadow-lg hover:shadow-teal-200 hover:scale-[1.02] transition-all"
                    type="submit"
                  >
                    Send Verification Code
                  </button>
                </form>
              ) : otpVerified ? (
                <form onSubmit={passwordFormik.handleSubmit}>
                  <div className="mb-6">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="h-5 w-5 text-teal-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v2h8z"
                          />
                        </svg>
                      </div>
                      <input
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                        placeholder="New Secure Password"
                        type="password"
                        name="newPassword"
                        value={passwordFormik.values.newPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                      />
                    </div>
                    {passwordFormik.touched.newPassword &&
                      passwordFormik.errors.newPassword && (
                        <div className="error text-red-500 text-sm mt-2 font-medium">
                          {passwordFormik.errors.newPassword}
                        </div>
                      )}
                  </div>

                  <button
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-white font-bold text-lg shadow-lg hover:shadow-teal-200 hover:scale-[1.02] transition-all"
                    type="submit"
                  >
                    Change Password
                  </button>
                </form>
              ) : (
                <form onSubmit={otpFormik.handleSubmit}>
                  <div className="mb-6">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="h-5 w-5 text-teal-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <input
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all tracking-[0.5em] text-center font-bold"
                        placeholder="000 000"
                        type="text"
                        name="otp"
                        maxLength="6"
                        value={otpFormik.values.otp}
                        onChange={otpFormik.handleChange}
                        onBlur={otpFormik.handleBlur}
                      />
                    </div>
                    {otpFormik.touched.otp && otpFormik.errors.otp && (
                      <div className="error text-red-500 text-sm mt-2 font-medium">
                        {otpFormik.errors.otp}
                      </div>
                    )}
                  </div>

                  <button
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-white font-bold text-lg shadow-lg hover:shadow-teal-200 hover:scale-[1.02] transition-all"
                    type="submit"
                  >
                    Verify Code
                  </button>
                </form>
              )}
              <div className="text-center mt-8 space-y-4">
                <p className="text-sm font-medium text-gray-600">
                  Suddenly remembered?{" "}
                  <Link
                    to="/login"
                    className="font-bold text-teal-600 hover:text-teal-700 hover:underline"
                  >
                    Return to Login
                  </Link>
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-sm font-medium text-gray-600">
                    New to Pulse Addis?{" "}
                    <Link
                      to="/register"
                      className="font-bold text-gray-900 hover:underline"
                    >
                      Create Account
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* End forgot password section */}
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
